import axios, { AxiosHeaders, InternalAxiosRequestConfig, AxiosError } from "axios";
import { useAuthStore } from "../store/authStore";
import { useUIStore } from "../store/uiStore";
import { refreshApi } from "./refreshApi";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // headers yoksa yeni AxiosHeaders oluştur
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    const method = config.method?.toLowerCase();
    const url = config.url || "";

    const isGlobalLoadForced = config.headers["x-global-load"] === "true";
    const isSilentForced = config.headers["x-silent"] === "true";

    let shouldShowLoader = true;

    if (isGlobalLoadForced) {
      shouldShowLoader = true;
    } else if (isSilentForced) {
      shouldShowLoader = false;
    } else {
      // Varsayılan mantık: GET istekleri sessizdir (Local component loader handle eder).
      if (method === "get") {
        shouldShowLoader = false;
      }
      // POST istekleri yeni kayıt oluşturma olabilir, ancak listeleme/rapor amaçlıysa sessizdir:
      else if (method === "post") {
        const queryKeywords = [
          "DynamicQuery",
          "Report",
          "Dashboard",
          "Summary",
          "LiveActionLogs",
          "ShortageAnalytics",
          "export"
        ];
        const isQueryPost = queryKeywords.some(keyword => url.includes(keyword));

        if (isQueryPost) {
          shouldShowLoader = false;
        }
      }
    }

    if (shouldShowLoader) {
      useUIStore.getState().setLoading(true);
      let msg = "İşlem yapılıyor...";
      if (url.includes("warehouse-task") || url.includes("task")) {
        msg = "Görev güncelleniyor...";
      } else if (url.includes("cargo") || url.includes("shipment")) {
        msg = "Kargo güncelleniyor...";
      } else if (url.includes("order")) {
        msg = "Sipariş işleniyor...";
      } else if (method === "put" || method === "patch") {
        msg = "Kayıt güncelleniyor...";
      } else if (method === "post") {
        msg = "Kaydediliyor...";
      } else if (method === "delete") {
        msg = "Siliniyor...";
      }
      useUIStore.getState().setSyncStatus('syncing', msg);
    }

    const accessToken = useAuthStore.getState().accessToken;

    if (accessToken) {
      config.headers.set("Authorization", `Bearer ${accessToken}`);
    }
    console.log("INTERCEPTOR REQ:", config.url, config.method)
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: { resolve: (value?: unknown) => void; reject: (err: any) => void }[] = [];

const processQueue = (error: any, accessToken: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(accessToken);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => {
    setTimeout(() => useUIStore.getState().setLoading(false), 200);
    const method = res.config.method?.toLowerCase();
    if (method && ["post", "put", "patch", "delete"].includes(method)) {
      useUIStore.getState().setSyncStatus('success', 'İşlem Başarılı');
      setTimeout(() => {
        if (useUIStore.getState().syncStatus === 'success') {
          useUIStore.getState().setSyncStatus('idle');
        }
      }, 1500);
    }
    console.log(res.data)
    return res;
  },
  async (err: AxiosError) => {
    const originalRequest = err.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const authStore = useAuthStore.getState();
    const uiStore = useUIStore.getState();
    useUIStore.getState().setLoading(false);
    if (err.response?.status === 403) {
      uiStore.setLoading(false);

      const isIgnored = originalRequest.headers && (
        (originalRequest.headers as any)["ignore-403"] === "true" ||
        (originalRequest.headers as any)["x-silent"] === "true"
      );

      if (isIgnored) {
        return Promise.reject(err);
      }

      const backendMessage = (err.response?.data as any)?.message;
      const finalMessage = backendMessage || err.message || "Bu işlem için yetkiniz bulunmamaktadır.";

      uiStore.setAccessDenied(true, finalMessage);

      return Promise.reject(err);
    }
    useUIStore.getState().setLoading(false);
    if (err.response?.status === 401 && !originalRequest._retry && originalRequest.url !== "/auth/login") {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) originalRequest.headers.set("Authorization", `Bearer ${token}`);
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await refreshApi.post("/auth/refresh", {
          refreshToken: authStore.refreshToken
        });

        const { accessToken: newToken, refreshToken: newRefreshToken } = res.data;


        authStore.setToken(newToken, newRefreshToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

        processQueue(null, newToken);

        if (originalRequest.headers) originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
        return api(originalRequest);
      } catch (e) {
        processQueue(e, null);
        authStore.logout();
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default api;
